import React from 'react';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import useTheme from '../../hooks/useTheme';

/**
 * ThemeToggle component - Allows users to switch between light and dark themes
 * 
 * Features:
 * - Toggle between light and dark modes
 * - Visual icon feedback (sun for light mode, moon for dark mode)
 * - Full keyboard accessibility (Enter/Space keys)
 * - ARIA attributes for screen readers
 * - Minimum 44x44px touch target for mobile accessibility
 * - Focus indicators with proper contrast
 * 
 * @component
 */
const ThemeToggle = () => {
  const { theme, toggleTheme } = useTheme();

  const handleKeyDown = (event) => {
    // Support Enter and Space keys for keyboard accessibility
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleTheme();
    }
  };

  const isDark = theme === 'dark';
  const label = `Switch to ${isDark ? 'light' : 'dark'} mode`;
  const currentThemeLabel = `${isDark ? 'Dark' : 'Light'} mode`;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={label}
      onClick={toggleTheme}
      onKeyDown={handleKeyDown}
      className="
        relative inline-flex items-center justify-center
        min-w-[44px] min-h-[44px] p-2
        rounded-lg
        bg-bg-secondary hover:bg-bg-tertiary
        border border-border-primary
        text-text-primary
        transition-colors duration-200
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
        focus:ring-offset-bg-primary
        active:scale-95
        cursor-pointer
      "
      title={label}
    >
      {/* Icon with smooth transition */}
      <span className="relative w-6 h-6" aria-hidden="true">
        {isDark ? (
          <SunIcon className="w-6 h-6 transition-transform duration-200 hover:rotate-45" />
        ) : (
          <MoonIcon className="w-6 h-6 transition-transform duration-200 hover:-rotate-12" />
        )}
      </span>

      {/* Screen reader only text */}
      <span className="sr-only">
        {currentThemeLabel}
      </span>
    </button>
  );
};

export default ThemeToggle;
